import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Button from "../../component/ui/Button";

// Swiper Imports
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";

// Swiper CSS
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

gsap.registerPlugin(ScrollTrigger);

const NewsSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const context = gsap.context(() => {
      const textElements = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".news-header-text")
      );
      textElements.forEach((element, index) => {
        gsap.set(element, {
          y: 25,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });
        gsap.to(element, {
          y: 0,
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)",
          duration: 1,
          ease: "power2.out",
          delay: index * 0.1,
          scrollTrigger: {
            trigger: sectionRef.current.querySelector(".news-header-container"),
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
      });
    }, sectionRef);
    return () => context.revert();
  }, []);

  const newsArticles = [
    {
      id: 1,
      title: "India's First",
      description: "CBG into the CGD pipeline network",
      image: "/images/Press/Press1.webp",
      link: "https://www.pib.gov.in/PressReleseDetail.aspx?PRID=1723513&reg=3&lang=2",
    },
    {
      id: 2,
      title: "IOCL",
      description: "Inauguration of CBG-CGD Synchronization",
      image: "/images/Press/Press2.webp",
      link: "https://www.iocl.com/NewsDetails/59269",
    },
    {
      id: 3,
      title: "CBG-CDG",
      description: "GAIL (India) Limited's Post",
      image: "/images/Press/Press3.webp",
      link: "https://www.linkedin.com/posts/gail-india-limited_narendramodi-gail-g20summitdelhi-activity-7106253264995725313-JYV7/?trk=public_profile_like_view",
    },
  ];

  const swiperParams = {
    modules: [Pagination, Navigation],
    loop: true,
    speed: 500,
    pagination: {
      el: ".custom-pagination", // Custom element for dots
      clickable: true,
    },
    navigation: {
      nextEl: ".custom-next", // Custom element for next arrow
      prevEl: ".custom-prev", // Custom element for prev arrow
    },
    slidesPerView: 1,
    centeredSlides: true,
    spaceBetween: 20,
    breakpoints: {
      320: { slidesPerView: 1, centeredSlides: true, spaceBetween: 15 },
      640: { slidesPerView: 2, centeredSlides: false, spaceBetween: 20 },
      1160: { slidesPerView: 3, centeredSlides: false, spaceBetween: 20 },
    },
  };

  return (
    <>
      <style global jsx>{`
        /* Desktop: Navigation hide */
        .mobile-nav-container {
          display: none;
        }

        /* Mobile Only Styles */
        @media (max-width: 640px) {
          .mobile-nav-container {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 20px;
            margin-top: 25px;
          }

          /* Dots Styling */
          .custom-pagination {
            display: flex !important;
            width: auto !important;
            gap: 8px;
          }
          .custom-pagination .swiper-pagination-bullet {
            background: #1c233a;
            opacity: 0.3;
            margin: 0 !important;
          }
          .custom-pagination .swiper-pagination-bullet-active {
            opacity: 1;
            width: 20px; /* Active dot wide effect */
            border-radius: 5px;
          }

          /* Arrow Buttons Styling */
          .custom-prev,
          .custom-next {
            color: #1c233a;
            font-size: 20px;
            font-weight: bold;
            cursor: pointer;
            user-select: none;
          }
        }
      `}</style>

      <section
        ref={sectionRef}
        className="w-auto bg-[#9fc46d] py-[40px] 2xl:py-[54px]"
      >
        {/* Header content unchanged */}
        <div className="news-header-container px-[32px] md:px-[112px] 2xl:px-[150px]">
          {/* ... (Header code same as before) ... */}
          <h2 className="text-[30px] font-bold text-[#1c233a]"></h2>
        </div>

        <div className="flex flex-col gap-[11px] sm:gap-[16px] 2xl:gap-[22px] px-[32px] md:px-[112px] 2xl:px-[150px] news-header-container">
          <div className="flex flex-row justify-start items-center w-full news-subtitle-container overflow-hidden">
            <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] font-[Sora] news-header-text text-[#1c233a]">
              Recognization
            </p>
            <div className="w-[100px] sm:w-[150px] 2xl:w-[200px] h-[1px] bg-[#1c233a]  ml-[15px] news-divider-line"></div>
          </div>
          <h2 className="text-[30px] sm:text-[45px] 2xl:text-[60px] font-bold text-[#1c233a] font-[Sora] news-header-text">
            Press
          </h2>
          <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal text-[#1c233a] font-[Sora] news-header-text">
            Government and industry announcements recognizing our large-scale
            CBG initiatives and clean energy contributions.
          </p>
        </div>

        <div className="mt-[30px] max-w-[1620px] m-auto px-[30px]">
          <Swiper {...swiperParams}>
            {newsArticles.map((article) => (
              <SwiperSlide key={article.id} className="pb-4">
                <div className="relative w-full h-[400px] sm:h-[430px] lg:h-[490px]">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="absolute top-0 w-full h-[300px]  sm:h-[356px] md:h-[300px] lg:h-[356px] rounded-[20px] object-fill md:object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-[#1C233A] rounded-[20px] p-[20px] text-white">
                    <h3 className="text-[20px] md:text-[24px] 2xl:text-[28px] font-bold">
                      {article.title}
                    </h3>
                    <p className="text-[14px] md:text-[16px] 2xl:text-[18px] mt-1">
                      {article.description}
                    </p>
                    <Button
                      text="Explore"
                      border_border="1px solid white"
                      className="mt-4"
                      text_color="white"
                      // Is line ko add karein
                      onClick={() =>
                        window.open(
                          article.link,
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                    />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Navigation & Pagination for Mobile */}
          <div className="mobile-nav-container">
            <div className="custom-prev">❮</div>
            <div className="custom-pagination"></div>
            <div className="custom-next">❯</div>
          </div>
        </div>
      </section>
    </>
  );
};

export default NewsSection;
